"use strict";
/**
 *
 * Copyright 2020-2025 Splunk Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SplunkDocumentLoadInstrumentation = void 0;
const instrumentation_document_load_1 = require("@opentelemetry/instrumentation-document-load");
const servertiming_1 = require("./servertiming");
const sdk_trace_web_1 = require("@opentelemetry/sdk-trace-web");
const core_1 = require("@opentelemetry/core");
const semantic_conventions_1 = require("@opentelemetry/semantic-conventions");
const excludedInitiatorTypes = ['beacon', 'fetch', 'xmlhttprequest'];
function addExtraDocLoadTags(span) {
    if (document.referrer && document.referrer !== '') {
        span.setAttribute('document.referrer', document.referrer);
    }
    if (window.screen) {
        span.setAttribute('screen.xy', window.screen.width + 'x' + window.screen.height);
    }
}
class SplunkDocumentLoadInstrumentation extends instrumentation_document_load_1.DocumentLoadInstrumentation {
    constructor(config = {}) {
        super(config);
        const exposedSuper = this;
        const _superEndSpan = exposedSuper._endSpan.bind(this);
        exposedSuper._onDocumentLoaded = (event) => {
            if (event && !event.isTrusted) {
                // React only to browser triggered load event
                return;
            }
            // Timeout is needed as load event doesn't have yet the performance metrics for loadEnd.
            // Support for event "loadend" is very limited and cannot be used
            window.setTimeout(() => {
                exposedSuper._collectPerformance();
            });
        };
        exposedSuper._endSpan = (span, performanceName, entries) => {
            var _a;
            // TODO: upstream exposed name on api.Span, then fix
            const exposedSpan = span;
            if (span) {
                span.setAttribute('component', this.component);
            }
            if (span && exposedSpan.name !== instrumentation_document_load_1.AttributeNames.DOCUMENT_LOAD) {
                // only apply links to document/resource fetch
                // To maintain compatibility, getEntries copies out select items from
                // different versions of the performance API into its own structure for the
                // initial document load (but leaves the entries undisturbed for resource loads).
                if (exposedSpan.name === instrumentation_document_load_1.AttributeNames.DOCUMENT_FETCH && performance.getEntriesByType) {
                    const navEntries = performance.getEntriesByType('navigation');
                    if (!entries.serverTiming && ((_a = navEntries[0]) === null || _a === void 0 ? void 0 : _a.serverTiming)) {
                        ;
                        entries.serverTiming = navEntries[0].serverTiming;
                    }
                    if (navEntries[0] &&
                        typeof navEntries[0].responseStatus === 'number' &&
                        navEntries[0].responseStatus > 0) {
                        span.setAttribute(semantic_conventions_1.SemanticAttributes.HTTP_STATUS_CODE, navEntries[0].responseStatus);
                    }
                }
                (0, servertiming_1.captureTraceParentFromPerformanceEntries)(entries, span);
                span.setAttribute(semantic_conventions_1.SemanticAttributes.HTTP_METHOD, 'GET');
            }
            if (span && exposedSpan.name === instrumentation_document_load_1.AttributeNames.DOCUMENT_LOAD) {
                addExtraDocLoadTags(span);
            }
            return _superEndSpan(span, performanceName, entries);
        };
        exposedSuper._initResourceSpan = (resource, parentSpan) => {
            var _a;
            if (excludedInitiatorTypes.indexOf(resource.initiatorType) !== -1 ||
                (0, core_1.isUrlIgnored)(resource.name, config.ignoreUrls)) {
                return;
            }
            const span = exposedSuper._startSpan(instrumentation_document_load_1.AttributeNames.RESOURCE_FETCH, sdk_trace_web_1.PerformanceTimingNames.FETCH_START, resource, parentSpan);
            if (span) {
                span.setAttribute(semantic_conventions_1.SEMATTRS_HTTP_URL, resource.name);
                if (!exposedSuper.getConfig().ignoreNetworkEvents) {
                    (0, sdk_trace_web_1.addSpanNetworkEvents)(span, resource);
                    if (typeof resource.responseStatus === 'number' && resource.responseStatus > 0) {
                        span.setAttribute(semantic_conventions_1.SemanticAttributes.HTTP_STATUS_CODE, resource.responseStatus);
                    }
                }
                exposedSuper._addCustomAttributesOnResourceSpan(span, resource, (_a = exposedSuper.getConfig().applyCustomAttributesOnSpan) === null || _a === void 0 ? void 0 : _a.resourceFetch);
                exposedSuper._endSpan(span, sdk_trace_web_1.PerformanceTimingNames.RESPONSE_END, resource);
            }
        };
    }
}
exports.SplunkDocumentLoadInstrumentation = SplunkDocumentLoadInstrumentation;
//# sourceMappingURL=SplunkDocumentLoadInstrumentation.js.map